import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Layout } from 'blaze-validator';
import BlazeInputReactSelect from 'blaze-input-react-select';
import { FormCheck } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import AuthService from '../../services/AuthService';
import FileNotFound from '../FileNotFound';
import QuoteHistoryList from './audit/QuoteHistoryList';
import '../../styles/quote.scss';

let layout;

const getLayout = () => {
  return ApiService.get('/db/EQS1/describe').then(response => {
    layout = new Layout(response.data.meta.pageLayout);
  });
};

const getData = quoteId => {
  return ApiService.get('/db/EQS1A', { params: { where: btoa(JSON.stringify({ quoteId })) } });
};

// TODO Cache or set to global storage?
const getStates = () => {
  return ApiService.get('/crm/states');
};

const defaultConfig = { showCalculated: true, showHidden: false, statusValue: 'all', assigneeValue: 'all' };

const statusOption = ({ label, color }) => (
  <div>
    {color && <i className="fad fa-circle" style={{ color, paddingRight: '3px' }} />}&nbsp;{label}
  </div>
);

const defaultStatusValue = { label: 'Show All', value: 'all' };
const defaultAssigneeValue = { label: 'Any User', value: 'all' };

const QuoteHistory = ({ quoteId }) => {
  // TODO don't pull from chat
  const { users } = useSelector(state => state.chat);

  const [ready, setReady] = useState(false);
  const [history, setHistory] = useState([]);
  const [states, setStates] = useState(null);
  const [filter, setFilter] = useState(defaultConfig);
  const [assigneeOptions, setAssigneeOptions] = useState([]);

  const handleShowCalculated = event => {
    setFilter(val => ({ ...val, showCalculated: event.target.checked }));
  };

  const handleShowHidden = event => {
    setFilter(val => ({ ...val, showHidden: event.target.checked }));
  };

  const handleStatusChange = (name, value) => {
    setFilter(val => ({ ...val, statusValue: value }));
  };

  const handleAssigneeChange = (name, value) => {
    setFilter(val => ({ ...val, assigneeValue: value }));
  };

  useEffect(() => {
    if (AuthService.hasPermission('EQS1A')) {
      getLayout().then(() => {
        getStates().then(response => {
          setStates(_.orderBy(response.data, 'order'));
        });

        getData(quoteId).then(response => {
          setHistory(response.data.list);
          setReady(true);
        });
      });
    }
  }, []);

  useEffect(() => {
    const historyUsers = {};
    for (const item of history) {
      if (!historyUsers[item.createdBy]) {
        if (item.createdBy === 0) {
          historyUsers[item.createdBy] = 'System';
        } else {
          const user = users.find(u => u.id === item.createdBy);
          if (user) {
            historyUsers[item.createdBy] = user.displayName;
          }
        }
      }
    }

    setAssigneeOptions([
      defaultAssigneeValue,
      ...Object.keys(historyUsers).map(userId => ({ label: historyUsers[userId], value: userId })),
    ]);
  }, [history]);

  if (!AuthService.hasPermission('EQS1A')) {
    return <FileNotFound>Missing permission for EQS1A</FileNotFound>;
  }

  if (!ready || states === null || !layout) {
    return <div className="quote-history" />;
  }

  if (!history.length) {
    return <div className="quote-history">Empty</div>;
  }

  const statusOptions = [
    defaultStatusValue,
    ...states.map(state => {
      return { label: state.name, color: state.color, value: state.id };
    }),
  ];

  const statusValue = statusOptions.find(o => o.value === filter.statusValue) || defaultStatusValue;
  const assigneeValue = assigneeOptions.find(o => o.value === filter.assigneeValue) || defaultAssigneeValue;

  return (
    <div className="quote-history">
      <div className="quote-history-filter">
        <BlazeInputReactSelect
          className="react-select-container flex-grow-1 mb-0"
          classNamePrefix="react-select"
          onChange={handleAssigneeChange}
          controlId="assignee-list"
          placeholder="Select Assignee"
          field={{
            name: 'assignee',
            label: '',
            options: assigneeOptions,
          }}
          value={assigneeValue}
        />
        <div className="setting-section">Data Filters</div>
        <div className="setting">
          <FormCheck>
            <FormCheck.Input
              type="checkbox"
              id="qh-calc"
              onChange={handleShowCalculated}
              defaultChecked={filter.showCalculated}
            />
            <FormCheck.Label htmlFor="qh-calc">Show Calculated</FormCheck.Label>
          </FormCheck>
        </div>
        <div className="setting border-bottom-0">
          <FormCheck>
            <FormCheck.Input
              type="checkbox"
              id="qh-hidden"
              onChange={handleShowHidden}
              defaultChecked={filter.showHidden}
            />
            <FormCheck.Label htmlFor="qh-hidden">Show Hidden</FormCheck.Label>
          </FormCheck>
        </div>
        <div className="setting-section">Status Filters</div>
        <div className="setting border-bottom-0">
          <BlazeInputReactSelect
            className="react-select-container flex-grow-1 mb-0"
            classNamePrefix="react-select"
            onChange={handleStatusChange}
            controlId="status-list"
            placeholder="Select Status"
            field={{
              name: 'states',
              label: '',
              inputProps: { formatOptionLabel: statusOption },
              options: statusOptions,
            }}
            value={statusValue}
          />
        </div>
      </div>
      <QuoteHistoryList layout={layout} states={states} history={history} filter={filter} />
    </div>
  );
};

QuoteHistory.propTypes = {
  quoteId: PropTypes.number,
};

export default QuoteHistory;
